import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import {
  setActiveTab,
  setCycleNumber,
  enqueueOffline,
  setSyncStatus,
  removeFromOfflineQueue,
} from "../store/backingPQISlice";
import {
  buildRecordsToSave,
  saveRecordsToDataverse,
  persistOfflineQueue,
  syncOfflineQueue,
} from "../Services/backingPQIDataverseService.js";
import { fetchBackingPQICycleDetails } from "../Services/BackingPQIService";

const TAB_LABELS = ["Creates", "Packs", "Product", "Net Weight"];
const TOTAL_CYCLES = 8;

const ITEMS_BY_TAB = {
  Creates: Array.from({ length: 15 }, (_, i) => `Create ${i + 1}`),
  Packs: Array.from({ length: 15 }, (_, i) => `PP ${i + 1}`),
  Product: Array.from({ length: 4 }, (_, i) => `P ${i + 1}`),
  "Net Weight": Array.from({ length: 15 }, (_, i) => `NW ${i + 1}`),
};

function getItemsForTab(tab) {
  return ITEMS_BY_TAB[tab] || ITEMS_BY_TAB.Creates;
}

export default function BackingPQI() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const plantTourId = useSelector((state) => state.planTour?.plantTourId);
  const selectedShift = useSelector((state) => state.planTour?.selectedCycle);
  const userName = useSelector((state) => state.user?.user?.Name) || "";
  const activeTab = useSelector((state) => state.backingPQI?.activeTab) || "Creates";
  const cycleNumber = useSelector((state) => state.backingPQI?.cycleNumber) ?? 1;
  const offlineQueue = useSelector((state) => state.backingPQI?.offlineQueue) || [];
  const syncStatus = useSelector((state) => state.backingPQI?.syncStatus) || "idle";

  const shiftValue = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("shiftValue") || "Shift 1" : "Shift 1";
  const displayShift = selectedShift || shiftValue;
  const headerDate = (() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })();

  const items = getItemsForTab(activeTab);

  // PQI-style: cycle status and active cycle per tab (Creates, Packs, Product, Net Weight)
  const defaultCycleStatus = () => {
    const s = {};
    for (let c = 1; c <= TOTAL_CYCLES; c++) {
      s[c] = { started: false, completed: false, defects: [], okays: [], submissions: [], detailRows: [] };
    }
    return s;
  };
  const [activeCycleByTab, setActiveCycleByTab] = useState(() => {
    const o = {};
    TAB_LABELS.forEach((tab) => { o[tab] = 1; });
    return o;
  });
  const activeCycle = activeCycleByTab[activeTab] ?? 1;
  const setActiveCycle = (next) => {
    setActiveCycleByTab((prev) => ({ ...prev, [activeTab]: next }));
    dispatch(setCycleNumber(next));
  };
  const [cycleStatus, setCycleStatus] = useState(() => {
    const o = {};
    TAB_LABELS.forEach((tab) => { o[tab] = defaultCycleStatus(); });
    return o;
  });
  const getStatusForTab = (tab, cycleNo) => cycleStatus[tab]?.[cycleNo] || { started: false, completed: false, defects: [], okays: [], submissions: [], detailRows: [], defectCategories: {}, defectRemarks: {} };
  const normalizeSubmissionField = (value) =>
    String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  const getSubmissionKey = (productName, batchNo) =>
    `${normalizeSubmissionField(productName)}|${normalizeSubmissionField(batchNo)}`;
  const dedupeSubmissionsByProduct = (subs) => {
    if (!subs || !subs.length) return [];
    const seen = new Set();
    return subs.filter((s) => {
      const key = getSubmissionKey(s.productName, s.batchNo);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const getSubmissionsForCycle = (subs, cycleNo) =>
    dedupeSubmissionsByProduct((subs || []).filter((s) => {
      const n = Number(s?.cycleNo);
      return Number.isFinite(n) ? n === cycleNo : true;
    }));
  const [selected, setSelected] = useState({});
  const [formFields, setFormFields] = useState({});
  const [productFormExpandedByCycle, setProductFormExpandedByCycle] = useState({});
  const [expandedCompletedCycle, setExpandedCompletedCycle] = useState(null);
  const [showSummaryDetails, setShowSummaryDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  const SUMMARY_LABEL_BY_TAB = {
    Creates: "Crates",
    Packs: "Pack",
    Product: "Product",
    "Net Weight": "Net wt.",
  };
  const getMaxPotentialScore = (tab, completedCycles) => {
    if (tab === "Creates") return 15 * 120 * completedCycles;
    if (tab === "Packs") return 15 * 120 * completedCycles;
    if (tab === "Product") return 4 * completedCycles * 120;
    if (tab === "Net Weight") return completedCycles * 120 * 15;
    return 0;
  };
  const WEIGHTAGE_PERCENT = {
    Creates: 15,
    Packs: 25,
    Product: 45,
    "Net Weight": 15,
  };
  const parseDefectCategoryType = (value) => {
    const v = String(value ?? "").trim().toUpperCase();
    if (!v) return "B";
    if (v === "CATEGORY A" || v === "A") return "A";
    if (v === "CATEGORY B" || v === "B") return "B";
    if (v.includes("A")) return "A";
    if (v.includes("B")) return "B";
    return "B";
  };
  const summaryByTab = TAB_LABELS.map((tab) => {
    let aDefects = 0;
    let bDefects = 0;
    let completedCycles = 0;
    for (let cycleNo = 1; cycleNo <= TOTAL_CYCLES; cycleNo++) {
      const status = getStatusForTab(tab, cycleNo);
      const subs = getSubmissionsForCycle(status.submissions || [], cycleNo);
      // Count cycle only when checklist data exists for that section+cycle.
      if (subs.length > 0) completedCycles += 1;
      subs.forEach((s) => {
        const categories = s?.defectCategories || {};
        Object.values(categories).forEach((cat) => {
          const t = parseDefectCategoryType(cat);
          if (t === "A") aDefects += 1;
          else bDefects += 1;
        });
      });
    }
    const maxScore = getMaxPotentialScore(tab, completedCycles);
    const scoreDeduction = aDefects * 90 + bDefects * 30;
    const scoreObtained = maxScore - scoreDeduction;
    let scorePercent = 0;
    if (tab === "Creates" || tab === "Packs") {
      scorePercent = maxScore !== 0 ? (scoreObtained / maxScore) * 100 : 0;
    } else {
      scorePercent = scoreDeduction !== 0 ? (scoreObtained / scoreDeduction) * 100 : 0;
    }
    let pqiScore = 0;
    if (tab === "Creates") pqiScore = scorePercent * 0.15;
    else if (tab === "Packs") pqiScore = scorePercent * 0.25;
    else if (tab === "Product") pqiScore = scorePercent * 0.45;
    else if (tab === "Net Weight") pqiScore = scorePercent * 0.15;
    return {
      tab,
      label: SUMMARY_LABEL_BY_TAB[tab] || tab,
      completedCycles,
      aDefects,
      bDefects,
      maxScore,
      scoreDeduction,
      scoreObtained,
      scorePercent,
      pqiScore,
    };
  });
  const summaryTotals = summaryByTab.reduce(
    (acc, t) => ({
      aDefects: acc.aDefects + t.aDefects,
      bDefects: acc.bDefects + t.bDefects,
      pqiScore: acc.pqiScore + t.pqiScore,
    }),
    { aDefects: 0, bDefects: 0, pqiScore: 0 }
  );
  const summaryStatus = summaryTotals.aDefects >= 2 ? "HOLD" : "PASS";

  // Sync activeCycle with Redux when tab or cycle changes
  useEffect(() => {
    dispatch(setCycleNumber(activeCycle));
  }, [activeCycle, activeTab, dispatch]);

  // Hydrate cycle status from backend so completed cycles reflect saved data
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!plantTourId) return;
    let cancelled = false;
    const run = async () => {
      const tokenRaw = localStorage.getItem("access_token");
      let accessToken = null;
      if (tokenRaw) {
        try {
          const parsed = JSON.parse(tokenRaw);
          if (parsed && parsed.token) accessToken = parsed.token;
        } catch (_) {}
      }
      if (!accessToken) {
        setHydrated(true);
        return;
      }
      try {
        const records = await fetchBackingPQICycleDetails(accessToken, plantTourId);
        if (cancelled || !records || records.length === 0) {
          setHydrated(true);
          return;
        }
        const cycleMatch = (cycleStr) => {
          if (!cycleStr || typeof cycleStr !== "string") return null;
          const m = /Cycle-(\d+)/i.exec(cycleStr);
          return m ? parseInt(m[1], 10) : null;
        };
        const byTabAndCycle = {};
        TAB_LABELS.forEach((tab) => {
          byTabAndCycle[tab] = {};
          for (let c = 1; c <= TOTAL_CYCLES; c++) byTabAndCycle[tab][c] = [];
        });
        records.forEach((r) => {
          const cycleNo = cycleMatch(r.cr3ea_cycle);
          if (cycleNo == null || cycleNo < 1 || cycleNo > TOTAL_CYCLES) return;
          const category = r.cr3ea_category && TAB_LABELS.includes(r.cr3ea_category) ? r.cr3ea_category : "Creates";
          if (!byTabAndCycle[category][cycleNo]) byTabAndCycle[category][cycleNo] = [];
          byTabAndCycle[category][cycleNo].push(r);
        });
        const nextStatus = {};
        TAB_LABELS.forEach((tab) => {
          nextStatus[tab] = defaultCycleStatus();
        });
        TAB_LABELS.forEach((tab) => {
          Object.keys(byTabAndCycle[tab] || {}).forEach((key) => {
          const cycleNo = parseInt(key, 10);
          const cycleRecords = (byTabAndCycle[tab] || {})[cycleNo] || [];
          if (cycleRecords.length === 0) return;
          const byProduct = {};
          cycleRecords.forEach((r) => {
            const pk = getSubmissionKey(r.cr3ea_productname, r.cr3ea_batchno);
            if (!byProduct[pk]) byProduct[pk] = [];
            byProduct[pk].push(r);
          });
          const submissions = [];
          const allDefects = [];
          const allOkays = [];
          const cycleDefectCategories = {};
          const cycleDefectRemarks = {};
          const detailRows = [];
          let setIndex = 0;
          Object.values(byProduct).forEach((recs) => {
            setIndex += 1;
            const defectLabels = [];
            const okayLabels = [];
            const defectCategories = {};
            const defectRemarks = {};
            recs.forEach((r) => {
              const s = (r.cr3ea_status ?? r.cr3ea_criteria ?? "").toString().trim();
              const sLower = s.toLowerCase();
              const label = (r.cr3ea_evaluationtype ?? r.cr3ea_packno ?? `Item ${r.cr3ea_itemno ?? ""}`.trim()) || `Create ${r.cr3ea_itemno ?? recs.indexOf(r) + 1}`;
              if (sLower === "not okay" || sLower === "notokay") {
                defectLabels.push(label);
                allDefects.push(label);
                const cat = r.cr3ea_defectcategory ?? "";
                const rem = r.cr3ea_majordefect ?? r.cr3ea_defectremarks ?? r.cr3ea_remarks ?? "";
                if (cat) {
                  defectCategories[label] = cat;
                  cycleDefectCategories[label] = cat;
                }
                if (rem) {
                  defectRemarks[label] = rem;
                  cycleDefectRemarks[label] = rem;
                }
              } else if (sLower === "okay") {
                okayLabels.push(label);
                allOkays.push(label);
              }
              detailRows.push({
                setIndex,
                itemNo: r.cr3ea_itemno ?? detailRows.length + 1,
                productName: r.cr3ea_productname ?? "",
                lineNo: r.cr3ea_lineno ?? "",
                batchNo: r.cr3ea_batchno ?? "",
                executiveName: r.cr3ea_executivename ?? "",
                packaged: r.cr3ea_packaged ?? r.cr3ea_pkd ?? "",
                expiry: r.cr3ea_expiry ?? r.cr3ea_expirydate ?? "",
                status: s || "",
                category: r.cr3ea_category ?? "",
                defectCategory: r.cr3ea_defectcategory ?? "",
                defect: r.cr3ea_defect ?? "",
                remarks: r.cr3ea_majordefect ?? r.cr3ea_defectremarks ?? r.cr3ea_remarks ?? "",
              });
            });
            const productName = recs[0]?.cr3ea_productname ?? "";
            const batchNo = recs[0]?.cr3ea_batchno ?? "";
            const missed = Math.max(0, recs.length - defectLabels.length - okayLabels.length);
            submissions.push({
              cycleNo,
              productName,
              batchNo,
              defects: defectLabels,
              okays: okayLabels,
              missedCount: missed,
              defectCategories,
              defectRemarks,
            });
          });
          nextStatus[tab][cycleNo] = {
            started: true,
            completed: true,
            defects: [...new Set(allDefects)],
            okays: [...new Set(allOkays)],
            submissions,
            detailRows,
            defectCategories: cycleDefectCategories,
            defectRemarks: cycleDefectRemarks,
          };
        });
        });
        if (!cancelled) {
          const store = (await import("../store/store")).default;
          const queue = store.getState()?.backingPQI?.offlineQueue || [];
          const applyOfflineToStatus = (statusObj) => {
            const out = {};
            TAB_LABELS.forEach((t) => { out[t] = { ...(statusObj[t] || defaultCycleStatus()) }; });
            (queue || []).forEach((item) => {
              const tab = item.category && TAB_LABELS.includes(item.category) ? item.category : "Creates";
              const cycleNo = Number(item.cycleNo);
              if (cycleNo < 1 || cycleNo > TOTAL_CYCLES) return;
              const records = item.records || [];
              const byProduct = {};
              records.forEach((r) => {
                const pk = getSubmissionKey(r.cr3ea_productname, r.cr3ea_batchno);
                if (!byProduct[pk]) byProduct[pk] = [];
                byProduct[pk].push(r);
              });
              const newSubs = [];
              const allD = [];
              const allO = [];
              Object.values(byProduct).forEach((recs) => {
                const dl = [];
                const ol = [];
                const dc = {};
                const dr = {};
                recs.forEach((r) => {
                  const s = (r.cr3ea_status ?? r.cr3ea_criteria ?? "").toString().trim().toLowerCase();
                  const label = (r.cr3ea_evaluationtype ?? r.cr3ea_packno ?? `Item ${r.cr3ea_itemno ?? ""}`) || `Item ${recs.indexOf(r) + 1}`;
                  if (s === "not okay" || s === "notokay") {
                    dl.push(label); allD.push(label);
                    if (r.cr3ea_defectcategory) dc[label] = r.cr3ea_defectcategory;
                    if (r.cr3ea_majordefect || r.cr3ea_defectremarks || r.cr3ea_remarks) dr[label] = r.cr3ea_majordefect ?? r.cr3ea_defectremarks ?? r.cr3ea_remarks ?? "";
                  } else if (s === "okay") { ol.push(label); allO.push(label); }
                });
                newSubs.push({
                  cycleNo,
                  productName: recs[0]?.cr3ea_productname ?? "",
                  batchNo: recs[0]?.cr3ea_batchno ?? "",
                  defects: dl,
                  okays: ol,
                  missedCount: Math.max(0, recs.length - dl.length - ol.length),
                  defectCategories: dc,
                  defectRemarks: dr,
                });
              });
              const ex = out[tab][cycleNo] || {};
              out[tab][cycleNo] = {
                ...ex,
                started: true,
                submissions: dedupeSubmissionsByProduct([...(ex.submissions || []), ...newSubs]),
                defects: [...new Set([...(ex.defects || []), ...allD])],
                okays: [...new Set([...(ex.okays || []), ...allO])],
              };
            });
            return out;
          };
          const mergedFromBackend = {};
          TAB_LABELS.forEach((tab) => {
            mergedFromBackend[tab] = { ...(nextStatus[tab] || defaultCycleStatus()) };
          });
          const merged = applyOfflineToStatus({ ...mergedFromBackend });
          setCycleStatus((prev) => {
            const combined = {};
            TAB_LABELS.forEach((tab) => {
              combined[tab] = { ...(prev[tab] || defaultCycleStatus()), ...(merged[tab] || {}) };
            });
            return combined;
          });
          setActiveCycleByTab((prev) => {
            const next = { ...prev };
            TAB_LABELS.forEach((tab) => {
              const tabStatus = nextStatus[tab] || {};
              const completedSet = new Set(Object.entries(tabStatus).filter(([, s]) => s.completed).map(([k]) => parseInt(k, 10)));
              let n = 1;
              while (n <= TOTAL_CYCLES && completedSet.has(n)) n++;
              next[tab] = Math.min(n, TOTAL_CYCLES);
            });
            return next;
          });
          const firstTab = TAB_LABELS[0];
          const firstNext = (() => {
            const tabStatus = nextStatus[firstTab] || {};
            const completedSet = new Set(Object.entries(tabStatus).filter(([, s]) => s.completed).map(([k]) => parseInt(k, 10)));
            let n = 1;
            while (n <= TOTAL_CYCLES && completedSet.has(n)) n++;
            return Math.min(n, TOTAL_CYCLES);
          })();
          dispatch(setCycleNumber(firstNext));
        }
      } catch (err) {
        console.warn("Backing PQI: hydrate from backend failed", err);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [plantTourId, dispatch]);

  // Apply offline queue to cycle status so offline-completed cycles show (e.g. after refresh before sync)
  useEffect(() => {
    if (!offlineQueue || offlineQueue.length === 0) return;
    setCycleStatus((prev) => {
      const next = {};
      TAB_LABELS.forEach((tab) => {
        next[tab] = { ...(prev[tab] || defaultCycleStatus()) };
      });
      offlineQueue.forEach((item) => {
        const tab = item.category && TAB_LABELS.includes(item.category) ? item.category : "Creates";
        const cycleNo = Number(item.cycleNo);
        if (cycleNo < 1 || cycleNo > TOTAL_CYCLES) return;
        const records = item.records || [];
        const byProduct = {};
        records.forEach((r) => {
          const pk = getSubmissionKey(r.cr3ea_productname, r.cr3ea_batchno);
          if (!byProduct[pk]) byProduct[pk] = [];
          byProduct[pk].push(r);
        });
        const newSubmissions = [];
        const allDefects = [];
        const allOkays = [];
        Object.values(byProduct).forEach((recs) => {
          const defectLabels = [];
          const okayLabels = [];
          const defectCategories = {};
          const defectRemarks = {};
          recs.forEach((r) => {
            const s = (r.cr3ea_status ?? r.cr3ea_criteria ?? "").toString().trim().toLowerCase();
            const label = (r.cr3ea_evaluationtype ?? r.cr3ea_packno ?? `Item ${r.cr3ea_itemno ?? ""}`) || `Item ${recs.indexOf(r) + 1}`;
            if (s === "not okay" || s === "notokay") {
              defectLabels.push(label);
              allDefects.push(label);
              const cat = r.cr3ea_defectcategory ?? "";
              const rem = r.cr3ea_majordefect ?? r.cr3ea_defectremarks ?? r.cr3ea_remarks ?? "";
              if (cat) defectCategories[label] = cat;
              if (rem) defectRemarks[label] = rem;
            } else if (s === "okay") {
              okayLabels.push(label);
              allOkays.push(label);
            }
          });
          const productName = recs[0]?.cr3ea_productname ?? "";
          const batchNo = recs[0]?.cr3ea_batchno ?? "";
          const missed = Math.max(0, recs.length - defectLabels.length - okayLabels.length);
          newSubmissions.push({
            cycleNo,
            productName,
            batchNo,
            defects: defectLabels,
            okays: okayLabels,
            missedCount: missed,
            defectCategories,
            defectRemarks,
          });
        });
        const existing = next[tab][cycleNo] || { started: false, completed: false, defects: [], okays: [], submissions: [] };
        const combinedSubs = dedupeSubmissionsByProduct([...(existing.submissions || []), ...newSubmissions]);
        next[tab][cycleNo] = {
          ...existing,
          started: true,
          submissions: combinedSubs,
          defects: [...new Set([...(existing.defects || []), ...allDefects])],
          okays: [...new Set([...(existing.okays || []), ...allOkays])],
        };
      });
      return next;
    });
  }, [offlineQueue]);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!online || offlineQueue.length === 0) return;
    let cancelled = false;
    const run = async () => {
      const tokenRaw = localStorage.getItem("access_token");
      let accessToken = null;
      if (tokenRaw) {
        try {
          const parsed = JSON.parse(tokenRaw);
          if (parsed && parsed.token) accessToken = parsed.token;
        } catch (_) {}
      }
      if (!accessToken) return;
      const store = (await import("../store/store")).default;
      const queue = store.getState()?.backingPQI?.offlineQueue || [];
      if (queue.length === 0 || cancelled) return;
      const { syncOfflineQueue: syncFn } = await import("../Services/backingPQIDataverseService.js");
      await syncFn(accessToken, queue, store.dispatch, removeFromOfflineQueue, setSyncStatus);
    };
    run();
    return () => { cancelled = true; };
  }, [online, offlineQueue.length]);

  const handleStart = (cycleNo) => {
    const initial = {};
    items.forEach((item) => {
      initial[item] = { status: null, category: null, defect: null, majorDefect: null, netWeight: null };
    });
    setCycleStatus((prev) => ({
      ...prev,
      [activeTab]: { ...(prev[activeTab] || defaultCycleStatus()), [cycleNo]: { ...(prev[activeTab]?.[cycleNo] || {}), started: true } },
    }));
    setSelected((prev) => ({ ...prev, [cycleNo]: initial }));
  };

  const handleSelect = (cycleNo, item, value) => {
    setSelected((prev) => ({
      ...prev,
      [cycleNo]: {
        ...prev[cycleNo],
        [item]: { ...(prev[cycleNo] || {})[item], status: value },
      },
    }));
  };

  const updateField = (cycleNo, item, field, value) => {
    setSelected((prev) => ({
      ...prev,
      [cycleNo]: {
        ...prev[cycleNo],
        [item]: { ...(prev[cycleNo] || {})[item], [field]: value },
      },
    }));
  };

  const handleFormFieldChange = (cycleNo, field, value) => {
    setFormFields((prev) => ({
      ...prev,
      [cycleNo]: { ...(prev[cycleNo] || {}), [field]: value },
    }));
  };

  const selectedToRows = (cycleNo) => {
    const sel = selected[cycleNo] || {};
    return items.map((label, i) => {
      const s = sel[label] || {};
      return {
        itemNum: i + 1,
        itemLabel: label,
        status: s.status ?? null,
        defectCategory: s.category ?? null,
        defect: s.defect ?? null,
        majorDefect: s.majorDefect ?? null,
        remarks: s.majorDefect ?? null,
        netWeight: s.netWeight ?? null,
      };
    });
  };

  const handleSaveProductChecklist = async (cycleNo) => {
    if (!plantTourId) {
      alert("No Plant Tour ID. Please start a plant tour first.");
      return;
    }
    const header = formFields[cycleNo] || {};
    const rows = selectedToRows(cycleNo);
    const recordsToSave = buildRecordsToSave(plantTourId, userName, activeTab, cycleNo, header, rows);
    if (recordsToSave.length === 0) {
      alert("No data to save.");
      return;
    }

    setSaving(true);
    try {
      if (online) {
        const tokenRaw = localStorage.getItem("access_token");
        let accessToken = null;
        if (tokenRaw) {
          try {
            const parsed = JSON.parse(tokenRaw);
            if (parsed && parsed.token) accessToken = parsed.token;
          } catch (_) {}
        }
        if (!accessToken) {
          dispatch(enqueueOffline({ category: activeTab, cycleNo, records: recordsToSave }));
          persistOfflineQueue([...offlineQueue, { id: `bpqi_${Date.now()}`, category: activeTab, cycleNo, records: recordsToSave, timestamp: Date.now() }]);
          alert("No access token. Data saved offline. Will sync when online.");
        } else {
          const result = await saveRecordsToDataverse(recordsToSave, accessToken);
          if (result.success) {
            alert("Product checklist saved.");
          } else {
            dispatch(enqueueOffline({ category: activeTab, cycleNo, records: recordsToSave }));
            persistOfflineQueue([...offlineQueue, { id: `bpqi_${Date.now()}`, category: activeTab, cycleNo, records: recordsToSave, timestamp: Date.now() }]);
            alert("Save failed. Data queued for offline sync.");
          }
        }
      } else {
        dispatch(enqueueOffline({ category: activeTab, cycleNo, records: recordsToSave }));
        persistOfflineQueue([...offlineQueue, { id: `bpqi_${Date.now()}`, category: activeTab, cycleNo, records: recordsToSave, timestamp: Date.now() }]);
        alert("Offline. Data saved locally and will sync when online.");
      }

      const defects = [];
      const okays = [];
      const defectCategories = {};
      const defectRemarks = {};
      Object.entries(selected[cycleNo] || {}).forEach(([key, val]) => {
        if (val.status === "Okay") okays.push(key);
        else if (val.status === "Not Okay") {
          defects.push(key);
          if (val.category) defectCategories[key] = val.category;
          if (val.majorDefect) defectRemarks[key] = val.majorDefect;
        }
      });
      const submission = {
        cycleNo,
        productName: header.product || "",
        batchNo: header.batchNo || "",
        defects,
        okays,
        missedCount: items.length - defects.length - okays.length,
        defectCategories,
        defectRemarks,
      };
      setCycleStatus((prev) => ({
        ...prev,
        [activeTab]: {
          ...(prev[activeTab] || {}),
          [cycleNo]: {
            ...(prev[activeTab]?.[cycleNo] || {}),
            submissions: [...(prev[activeTab]?.[cycleNo]?.submissions || []), submission],
            defects: [...(prev[activeTab]?.[cycleNo]?.defects || []), ...defects],
            okays: [...(prev[activeTab]?.[cycleNo]?.okays || []), ...okays],
          },
        },
      }));
      setSelected((prev) => ({ ...prev, [cycleNo]: {} }));
      setProductFormExpandedByCycle((prev) => ({ ...prev, [cycleNo]: false }));
    } catch (err) {
      alert("Error saving: " + (err && err.message ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteCycle = (cycleNo) => {
    setCycleStatus((prev) => ({
      ...prev,
      [activeTab]: { ...(prev[activeTab] || {}), [cycleNo]: { ...(prev[activeTab]?.[cycleNo] || {}), completed: true } },
    }));
    setActiveCycleByTab((prev) => {
      const tabStatus = { ...(cycleStatus[activeTab] || {}), [cycleNo]: { ...(cycleStatus[activeTab]?.[cycleNo] || {}), completed: true } };
      const completed = new Set(Object.entries(tabStatus).filter(([, s]) => s.completed).map(([k]) => parseInt(k, 10)));
      let next = 1;
      while (next <= TOTAL_CYCLES && completed.has(next)) next++;
      return { ...prev, [activeTab]: Math.min(next, TOTAL_CYCLES) };
    });
    const tabStatus = { ...(cycleStatus[activeTab] || {}), [cycleNo]: { ...(cycleStatus[activeTab]?.[cycleNo] || {}), completed: true } };
    const completed = new Set(Object.entries(tabStatus).filter(([, s]) => s.completed).map(([k]) => parseInt(k, 10)));
    let next = 1;
    while (next <= TOTAL_CYCLES && completed.has(next)) next++;
    dispatch(setCycleNumber(Math.min(next, TOTAL_CYCLES)));
  };

  const handleExpand = (cycleNo) => {
    setExpandedCompletedCycle(cycleNo);
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 px-4 md:px-6 py-4">
        {/* Back link */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/home"))}
            className="flex items-center gap-1 text-gray-800 hover:text-gray-600 font-semibold bg-transparent border-0 cursor-pointer p-0"
          >
            <span className="text-lg">←</span>
            <span>Back</span>
          </button>
        </div>

        {/* Header bar: heading with shift + date on left (Product Quality Index / Bakery style) */}
        <div className="flex flex-nowrap items-center gap-4 px-4 py-3 mb-6 rounded-lg bg-gray-100 border border-gray-200 shadow-sm min-h-[3rem]">
          <h1 className="text-xl font-bold text-gray-900 shrink-0">Product Quality Index (Bakery)</h1>
          <div className="flex flex-nowrap items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-200/90 text-gray-800 text-sm font-medium border border-gray-300/80 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-600 shrink-0" aria-hidden="true" />
              {displayShift}
            </span>
            <span className="text-gray-500 font-medium" aria-hidden="true">•</span>
            <span className="text-sm text-gray-900 font-medium tabular-nums">{headerDate}</span>
          </div>
        </div>

        {/* Summary Section - same model as Product Quality Index */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 overflow-x-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-base sm:text-lg">Summary</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 text-sm font-medium">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9l-6 6m0-6l6 6" />
                </svg>
                {summaryTotals.aDefects + summaryTotals.bDefects} Defects
              </span>
            </div>
            <button
              type="button"
              className="text-blue-700 text-sm font-medium underline"
              onClick={() => setShowSummaryDetails((v) => !v)}
            >
              {showSummaryDetails ? "View less" : "View more"}
            </button>
          </div>
          {showSummaryDetails && (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full text-sm border border-gray-300">
                <thead>
                  <tr className="bg-amber-100 border-b border-gray-300">
                    <th className="py-2 px-2 border-r border-gray-300 font-semibold text-center" colSpan={7}>
                      PQI Calculator
                    </th>
                  </tr>
                  <tr className="bg-amber-100 border-b border-gray-300">
                    <th className="py-2 px-3 border-r border-gray-300 text-left font-semibold"></th>
                    <th className="py-2 px-3 border-r border-gray-300 text-left font-semibold">Nos of Hrs inspection/production</th>
                    {summaryByTab.map((row) => (
                      <th key={row.tab} className="py-2 px-3 border-r border-gray-300 font-semibold text-center">
                        {row.label}
                      </th>
                    ))}
                    <th className="py-2 px-3 font-semibold text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 font-semibold bg-red-500">A</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300"></td>
                    {summaryByTab.map((row) => (
                      <td key={`a_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center font-semibold bg-red-500">
                        {row.aDefects}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center font-semibold">{summaryTotals.aDefects}</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 font-semibold bg-yellow-100">B</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300"></td>
                    {summaryByTab.map((row) => (
                      <td key={`b_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center font-semibold bg-yellow-100">
                        {row.bDefects}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center font-semibold">{summaryTotals.bDefects}</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">Nos of Hrs inspection/production</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center">Completed cycles</td>
                    {summaryByTab.map((row) => (
                      <td key={`hrs_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.completedCycles}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center"></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">Max Potential Score</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center">By formula</td>
                    {summaryByTab.map((row) => (
                      <td key={`max_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.maxScore}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center"></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">Score deduction</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center"></td>
                    {summaryByTab.map((row) => (
                      <td key={`ded_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.scoreDeduction}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center"></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">Score obtained</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center"></td>
                    {summaryByTab.map((row) => (
                      <td key={`obt_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.scoreObtained}
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center"></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">Score%</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center"></td>
                    {summaryByTab.map((row) => (
                      <td key={`pct_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.scorePercent.toFixed(0)}%
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center"></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold">PQI Score as per weightage</td>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-center"></td>
                    {summaryByTab.map((row) => (
                      <td key={`pqi_${row.tab}`} className="py-2 px-3 border-r border-t border-gray-300 text-center">
                        {row.pqiScore.toFixed(2)}%
                      </td>
                    ))}
                    <td className="py-2 px-3 border-t border-gray-300 text-center font-semibold">{summaryTotals.pqiScore.toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold bg-red-200">Final PQI Score</td>
                    <td className="py-2 px-3 border-t border-gray-300 text-center font-semibold" colSpan={6}>
                      {summaryTotals.pqiScore.toFixed(2)}%
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border-r border-t border-gray-300 text-left font-semibold bg-sky-200">PQI Status</td>
                    <td className="py-2 px-3 border-t border-gray-300 text-center" colSpan={6}>
                      <span className={`inline-block min-w-[120px] text-center px-4 py-1 rounded-full text-white font-bold text-sm ${summaryStatus === "PASS" ? "bg-green-500" : "bg-red-500"}`}>
                        {summaryStatus}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sync status badges */}
        <div className="mb-4 flex items-center justify-end gap-2 flex-wrap">
          {offlineQueue.length > 0 && (
            <span className="text-amber-700 bg-amber-100 px-3 py-1 rounded-full text-sm">
              {offlineQueue.length} pending sync
            </span>
          )}
          {syncStatus === "syncing" && <span className="text-blue-600 text-sm">Syncing...</span>}
          {syncStatus === "success" && <span className="text-green-600 text-sm">Sync complete</span>}
          {syncStatus === "failed" && <span className="text-red-600 text-sm">Sync had errors</span>}
        </div>

        <div className="flex flex-col md:grid md:grid-cols-4 gap-4">
          <div className="space-y-2 md:col-span-1">
            {TAB_LABELS.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
                  activeTab === tab ? "bg-blue-100 text-blue-600 border-blue-200" : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => dispatch(setActiveTab(tab))}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="md:col-span-3 space-y-6 max-h-[70vh] overflow-y-auto">
            {Array.from({ length: TOTAL_CYCLES }, (_, i) => {
              const cycleNo = i + 1;
              const status = getStatusForTab(activeTab, cycleNo);
              const shouldShow = status.completed || cycleNo === activeCycle || cycleNo === activeCycle + 1;
              if (!shouldShow) return null;
              const isDisabled = !status.completed && cycleNo !== activeCycle;
              const cycleSubmissions = getSubmissionsForCycle(status.submissions || [], cycleNo);

              return (
                <div
                  key={cycleNo}
                  className={`border rounded-md p-4 shadow-sm ${isDisabled ? "bg-gray-100 opacity-60" : "bg-white"}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-md font-semibold">Cycle {cycleNo}</h2>
                  </div>

                  {/* Completed product(s) summary + Complete Cycle / Add New Product (hide when form expanded for new product) */}
                  {(() => {
                    const uniqueSubmissions = cycleSubmissions;
                    return !status.completed && cycleNo === activeCycle && status.started && Object.keys(selected[cycleNo] || {}).length === 0 && uniqueSubmissions.length >= 1 && !productFormExpandedByCycle[cycleNo] && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="space-y-3 mb-3">
                        {uniqueSubmissions.map((sub, idx) => (
                          <div key={idx} className="p-3 bg-white border border-gray-200 rounded-lg">
                            <div className="font-medium text-gray-800 mb-2">
                              Product {idx + 1}{sub.productName ? ` — ${sub.productName}` : ""}{sub.batchNo ? ` (Batch: ${sub.batchNo})` : ""}
                            </div>
                            <div className="flex flex-wrap gap-2 text-sm">
                              {sub.defects?.length > 0 && <span className="text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{sub.defects.length} Defect(s)</span>}
                              {sub.okays?.length > 0 && <span className="text-green-600 bg-green-100 px-2 py-0.5 rounded-full">{sub.okays.length} Okay(s)</span>}
                              {sub.missedCount > 0 && <span className="text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">{sub.missedCount} Missed</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-green-200">
                        <span className="text-green-700 font-semibold">✓ {uniqueSubmissions.length} Product checklist(s) completed</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setProductFormExpandedByCycle((prev) => ({ ...prev, [cycleNo]: true }))}
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                          >
                            Add New Product
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCompleteCycle(cycleNo)}
                            className="px-4 py-2 bg-green-600 text-white rounded font-medium"
                          >
                            Complete Cycle & Move to Next
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                  })()}

                  {/* Header form + Start Session / Add New Product (hide when green summary card is shown to avoid duplicate Add New Product) */}
                  {!status.completed && cycleNo === activeCycle && (!status.started || (Object.keys(selected[cycleNo] || {}).length === 0 && cycleSubmissions.length >= 1)) && (productFormExpandedByCycle[cycleNo] || !status.started || cycleSubmissions.length < 1) && (
                    <div className="space-y-6">
                      {(!status.started || productFormExpandedByCycle[cycleNo]) ? (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">Product</label>
                              <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Enter Product"
                                value={formFields[cycleNo]?.product || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "product", e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Batch No</label>
                              <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Enter Batch No"
                                value={formFields[cycleNo]?.batchNo || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "batchNo", e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Executive Name</label>
                              <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Enter Executive Name"
                                value={formFields[cycleNo]?.executiveName || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "executiveName", e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Line No</label>
                              <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Enter Line No"
                                value={formFields[cycleNo]?.lineNo || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "lineNo", e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Packaged</label>
                              <input
                                type="date"
                                className="w-full border rounded px-3 py-2"
                                value={formFields[cycleNo]?.packaged || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "packaged", e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Expiry</label>
                              <input
                                type="date"
                                className="w-full border rounded px-3 py-2"
                                value={formFields[cycleNo]?.expiry || ""}
                                onChange={(e) => handleFormFieldChange(cycleNo, "expiry", e.target.value)}
                              />
                            </div>
                            {activeTab === "Net Weight" && (
                              <div>
                                <label className="block text-sm font-medium mb-1">Standard Weight</label>
                                <input
                                  type="text"
                                  className="w-full border rounded px-3 py-2"
                                  value={formFields[cycleNo]?.standardWeight || ""}
                                  onChange={(e) => handleFormFieldChange(cycleNo, "standardWeight", e.target.value)}
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex justify-end">
                            <button type="button" onClick={() => handleStart(cycleNo)} className="px-4 py-2 bg-blue-600 text-white rounded">
                              {cycleSubmissions.length >= 1 ? "Add New Product" : "Start Session"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setProductFormExpandedByCycle((prev) => ({ ...prev, [cycleNo]: true }))}
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                          >
                            Add New Product
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Checklist when started and has selections */}
                  {status.started && !status.completed && Object.keys(selected[cycleNo] || {}).length > 0 && (
                    <div className="space-y-6 mt-4">
                      {items.map((item) => {
                        const current = selected[cycleNo]?.[item];
                        return (
                          <div key={item} className="border p-4 rounded">
                            <div className="flex justify-between items-center mb-2 gap-4">
                              <div className="flex items-center gap-3 flex-1">
                                <h3 className="font-semibold">{item}</h3>
                                {activeTab === "Net Weight" && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700">Net Weight:</label>
                                    <input
                                      type="text"
                                      className="w-32 border rounded px-3 py-1 text-sm"
                                      placeholder="Weight"
                                      value={current?.netWeight || ""}
                                      onChange={(e) => updateField(cycleNo, item, "netWeight", e.target.value)}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSelect(cycleNo, item, "Not Okay")}
                                  className={`px-3 py-1 rounded border ${current?.status === "Not Okay" ? "bg-red-100 border-red-600 text-red-600" : "border-red-400 text-red-500"}`}
                                >
                                  Not Okay
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSelect(cycleNo, item, "Okay")}
                                  className={`px-3 py-1 rounded border ${current?.status === "Okay" ? "bg-green-100 border-green-600 text-green-600" : "border-green-400 text-green-500"}`}
                                >
                                  Okay
                                </button>
                              </div>
                            </div>
                            {current?.status === "Not Okay" && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Select Category</label>
                                  <select
                                    className="w-full border rounded px-3 py-2"
                                    value={current.category || ""}
                                    onChange={(e) => updateField(cycleNo, item, "category", e.target.value)}
                                  >
                                    <option value="">Select</option>
                                    <option value="Category A">Category A</option>
                                    <option value="Category B">Category B</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Defect</label>
                                  <input
                                    type="text"
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Enter Defect"
                                    value={current.defect || ""}
                                    onChange={(e) => updateField(cycleNo, item, "defect", e.target.value)}
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <label className="block text-sm font-medium mb-1">Major Defect and Remarks</label>
                                  <input
                                    type="text"
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Enter Major Defect or Remarks"
                                    value={current.majorDefect || ""}
                                    onChange={(e) => updateField(cycleNo, item, "majorDefect", e.target.value)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex justify-end gap-2 mt-4 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleSaveProductChecklist(cycleNo)}
                          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Product Checklist"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Completed cycle summary (collapsible) — same arrow and layout as Product Quality checklist */}
                  {status.completed && (
                    <div className="border-t pt-4">
                      <div
                        className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        onClick={() => handleExpand(expandedCompletedCycle === cycleNo ? null : cycleNo)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-semibold">✓ Completed</span>
                            {cycleSubmissions.length > 1 && (
                              <span className="text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">
                                {cycleSubmissions.length} product checklists
                              </span>
                            )}
                            {status.defects?.length > 0 && (
                              <span className="text-red-600 bg-red-100 px-2 py-0.5 rounded-full text-xs font-medium">
                                {status.defects.length} Defect(s)
                              </span>
                            )}
                            {status.okays?.length > 0 && (
                              <span className="text-green-600 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
                                {status.okays.length} Okay(s)
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 focus:outline-none p-1"
                          tabIndex={-1}
                          onClick={(e) => { e.stopPropagation(); handleExpand(expandedCompletedCycle === cycleNo ? null : cycleNo); }}
                        >
                          <svg className={`w-5 h-5 transform transition-transform duration-200 ${expandedCompletedCycle === cycleNo ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      {expandedCompletedCycle === cycleNo && (
                        <div className="pt-4 space-y-4">
                          {(() => {
                            const uniqueSubs = cycleSubmissions;
                            return uniqueSubs.length > 0 ? (
                            uniqueSubs.map((sub, subIdx) => (
                              <div key={subIdx} className="p-4 bg-white border border-gray-200 rounded-lg space-y-3">
                                <div className="font-semibold text-gray-800 border-b pb-2">
                                  Product {subIdx + 1}{sub.productName ? ` — ${sub.productName}` : ""}{sub.batchNo ? ` (Batch: ${sub.batchNo})` : ""}
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <div className="text-red-600 font-semibold mb-1 text-sm">Defects ({sub.defects?.length ?? 0})</div>
                                    {(sub.defects?.length ?? 0) > 0 ? (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm border rounded-md">
                                          <thead className="bg-red-50">
                                            <tr>
                                              <th className="px-3 py-1.5 border font-medium">Item No</th>
                                              <th className="px-3 py-1.5 border font-medium">Category</th>
                                              <th className="px-3 py-1.5 border font-medium">Remarks</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sub.defects.map((defect, i) => (
                                              <tr key={i}>
                                                <td className="px-3 py-1.5 border">{defect}</td>
                                                <td className="px-3 py-1.5 border">{sub.defectCategories?.[defect] ?? "—"}</td>
                                                <td className="px-3 py-1.5 border">{sub.defectRemarks?.[defect] ?? "—"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 text-sm py-1">None</div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-green-700 font-semibold mb-1 text-sm">Okays ({sub.okays?.length ?? 0})</div>
                                    {(sub.okays?.length ?? 0) > 0 ? (
                                      <div className="bg-green-50 p-3 rounded">
                                        <span className="text-green-800 text-sm">{sub.okays.join(", ")}</span>
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 text-sm py-1">None</div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-yellow-700 font-semibold mb-1 text-sm">Missed items ({sub.missedCount ?? 0})</div>
                                    {(sub.missedCount ?? 0) > 0 ? (
                                      <div className="bg-yellow-50 p-3 rounded">
                                        <span className="text-yellow-800 text-sm">{sub.missedCount} item(s) not evaluated</span>
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 text-sm py-1">None</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : null;
                          })()}
                          {cycleSubmissions.length === 0 && (
                            <>
                              {status.defects?.length > 0 && (
                                <div className="mb-4">
                                  <div className="text-red-600 font-semibold mb-2 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    Defects ({status.defects.length})
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm border rounded-md">
                                      <thead className="bg-red-50">
                                        <tr>
                                          <th className="px-4 py-2 border font-medium">Item No</th>
                                          <th className="px-4 py-2 border font-medium">Defect Category</th>
                                          <th className="px-4 py-2 border font-medium">Defect</th>
                                          <th className="px-4 py-2 border font-medium">Major Defect</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {status.defects.map((defect, index) => (
                                          <tr key={index} className="bg-white hover:bg-gray-50">
                                            <td className="px-4 py-2 border font-medium text-gray-700">{defect}</td>
                                            <td className="px-4 py-2 border">{status.defectCategories?.[defect] ?? "—"}</td>
                                            <td className="px-4 py-2 border">—</td>
                                            <td className="px-4 py-2 border">{status.defectRemarks?.[defect] ?? "—"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="bg-green-50 p-4 rounded-lg">
                                  <div className="text-green-700 font-semibold mb-2 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Okays ({status.okays?.length ?? 0})
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    {(status.okays?.length ?? 0) > 0 ? (
                                      status.okays.map((okay, i) => (
                                        <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                                          {okay}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-gray-400 italic">No Okays</span>
                                    )}
                                  </div>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                  <div className="text-yellow-700 font-semibold mb-2 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    Missed ({cycleSubmissions.reduce((acc, s) => acc + (s.missedCount ?? 0), 0)})
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    <span className="text-gray-400 italic">No Missed Items</span>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
